import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAllReviews, fetchBooks } from "@/lib/api";
import { Star, Loader2, MessageSquareQuote } from "lucide-react";

const Stars = ({ value = 0 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className={`w-4 h-4 ${
          s <= value
            ? "fill-[var(--bw-accent)] text-[var(--bw-accent)]"
            : "text-[var(--bw-border)]"
        }`}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [booksMap, setBooksMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(0); // 0 = all

  useEffect(() => {
    Promise.all([fetchAllReviews(), fetchBooks()])
      .then(([r, b]) => {
        setReviews(r);
        const m = {};
        b.forEach((x) => (m[x.id] = x));
        setBooksMap(m);
      })
      .finally(() => setLoading(false));
  }, []);

  const visible =
    filter === 0 ? reviews : reviews.filter((r) => r.rating === filter);

  const avg =
    reviews.length > 0
      ? reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length
      : 0;

  return (
    <main
      data-testid="reviews-page"
      className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16"
    >
      <section className="border-b border-[var(--bw-primary)]/20 pb-10">
        <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)]">
          — the wall of reviews
        </p>
        <h1
          className="font-serif-display leading-[0.85] tracking-tight mt-4"
          style={{ fontSize: "clamp(3rem, 9vw, 8rem)" }}
        >
          What readers say.
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <Stars value={Math.round(avg)} />
            <span className="font-mono-plex text-sm text-[var(--bw-secondary)]">
              {reviews.length > 0
                ? `${avg.toFixed(1)} average across ${reviews.length} reviews`
                : "No reviews yet"}
            </span>
          </div>
        </div>
      </section>

      <section className="mt-10 flex flex-wrap items-center gap-2">
        {[0, 5, 4, 3, 2, 1].map((s) => (
          <button
            key={s}
            data-testid={`reviews-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
              filter === s
                ? "bg-[var(--bw-primary)] text-[var(--bw-bg)] border-[var(--bw-primary)]"
                : "border-[var(--bw-border)] hover:border-[var(--bw-primary)]"
            }`}
          >
            {s === 0 ? "All ratings" : `${s} star${s > 1 ? "s" : ""}`}
          </button>
        ))}
      </section>

      <section className="mt-10">
        {loading ? (
          <div className="flex items-center gap-3 text-[var(--bw-secondary)] py-20 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-mono-plex text-sm">loading reviews</span>
          </div>
        ) : visible.length === 0 ? (
          <div
            data-testid="reviews-page-empty"
            className="border border-dashed border-[var(--bw-border)] p-16 text-center"
          >
            <MessageSquareQuote className="w-8 h-8 text-[var(--bw-accent)] mx-auto" />
            <p className="font-serif-display text-3xl mt-4">
              Nothing here yet.
            </p>
            <p className="text-sm text-[var(--bw-secondary)] mt-2">
              Head to a book and leave the first review.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {visible.map((r) => {
              const book = booksMap[r.book_id];
              return (
                <article
                  key={r.id}
                  data-testid={`reviews-page-item-${r.id}`}
                  className="border border-[var(--bw-border)] bg-[var(--bw-surface)] p-6 flex gap-5"
                >
                  {book && (
                    <Link
                      to={`/book/${book.id}`}
                      className="shrink-0 w-16 h-24 overflow-hidden border border-[var(--bw-border)]"
                    >
                      <img
                        src={book.cover_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </Link>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-serif-display text-lg leading-tight">
                          {r.reviewer_name}
                        </p>
                        <p className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)] mt-1">
                          {new Date(r.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Stars value={r.rating} />
                    </div>
                    {book && (
                      <Link
                        to={`/book/${book.id}`}
                        className="mt-2 inline-block text-xs text-[var(--bw-accent)] hover:underline"
                      >
                        on “{book.title}”
                      </Link>
                    )}
                    <p className="mt-3 text-sm leading-relaxed">
                      {r.comment}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default Reviews;
